/**
 * AI Model Configuration Types
 * Maps technical model names to user-friendly labels
 */

export type AIModelTechnicalName = 
  | 'gpt-4o-mini'      // Current default (fast, économique)
  | 'gpt-4o'           // Premium (plus intelligent)
  | 'gpt-4-turbo'      // Legacy premium
  | 'gpt-3.5-turbo'    // Legacy économique

export type AIModelTier = 'standard' | 'premium' | 'pro'

export interface AIModelConfig {
  /** Technical OpenAI model name */
  technicalName: AIModelTechnicalName
  
  /** User-friendly display name (French) */
  displayName: string
  
  /** Short description for users */
  description: string
  
  /** Plan tier required to access this model */
  requiredTier: AIModelTier
  
  /** Cost multiplier relative to base model (gpt-4o-mini = 1.0) */
  costMultiplier: number
  
  /** Performance characteristics */
  performance: {
    /** Average response time (ms) */
    avgResponseMs: number
    
    /** Quality score (0-100) */
    qualityScore: number
    
    /** Token limit */
    maxTokens: number
  }
  
  /** Is this model deprecated? */
  deprecated?: boolean
  
  /** Is this model currently available? */
  enabled: boolean
}

export interface AIModelSelection {
  /** Selected model technical name */
  model: AIModelTechnicalName
  
  /** User-friendly label to display */
  displayName: string
  
  /** Reason for selection (auto-selected, user-selected, plan-default) */
  selectionReason: 'auto' | 'user' | 'plan-default'
}

/**
 * Available AI models configuration
 */
export const AI_MODELS: Record<AIModelTechnicalName, AIModelConfig> = {
  'gpt-4o-mini': {
    technicalName: 'gpt-4o-mini',
    displayName: 'Explorer IA',
    description: 'Rapide et économique - Idéal pour débuter',
    requiredTier: 'standard',
    costMultiplier: 1,
    performance: {
      avgResponseMs: 1500,
      qualityScore: 75,
      maxTokens: 16384
    },
    enabled: true
  },
  
  'gpt-4o': {
    technicalName: 'gpt-4o',
    displayName: 'Navigator Boost',
    description: 'Intelligence avancée - Suggestions optimales',
    requiredTier: 'premium',
    costMultiplier: 3,
    performance: {
      avgResponseMs: 2500,
      qualityScore: 90,
      maxTokens: 128000
    },
    enabled: true
  },
  
  'gpt-4-turbo': {
    technicalName: 'gpt-4-turbo',
    displayName: 'GPT-4 Turbo Premium',
    description: 'Puissance maximale - Plans complexes',
    requiredTier: 'pro',
    costMultiplier: 5,
    performance: {
      avgResponseMs: 3000,
      qualityScore: 95,
      maxTokens: 128000
    },
    enabled: true
  },
  
  'gpt-3.5-turbo': {
    technicalName: 'gpt-3.5-turbo',
    displayName: 'GPT-3.5 (Ancien)',
    description: 'Modèle legacy - Non recommandé',
    requiredTier: 'standard',
    costMultiplier: 0.5,
    performance: {
      avgResponseMs: 1000,
      qualityScore: 60,
      maxTokens: 16385
    },
    deprecated: true,
    enabled: false
  }
}

/**
 * Get user-friendly model name from technical name
 */
export function getModelDisplayName(technicalName: string): string {
  const model = AI_MODELS[technicalName as AIModelTechnicalName]
  return model?.displayName ?? technicalName
}

/**
 * Get model config by technical name
 */
export function getModelConfig(technicalName: string): AIModelConfig | undefined {
  return AI_MODELS[technicalName as AIModelTechnicalName]
}

/**
 * Get available models for a plan tier
 */
export function getModelsForTier(tier: AIModelTier): AIModelConfig[] {
  const tierHierarchy: Record<AIModelTier, AIModelTier[]> = {
    'standard': ['standard'],
    'premium': ['standard', 'premium'],
    'pro': ['standard', 'premium', 'pro']
  }
  
  const allowedTiers = tierHierarchy[tier] || ['standard']
  
  return Object.values(AI_MODELS)
    .filter(model => 
      model.enabled && 
      !model.deprecated && 
      allowedTiers.includes(model.requiredTier)
    )
    .sort((a, b) => b.performance.qualityScore - a.performance.qualityScore)
}

/**
 * Get default model for a plan tier
 */
export function getDefaultModelForTier(tier: AIModelTier): AIModelTechnicalName {
  const defaults: Record<AIModelTier, AIModelTechnicalName> = {
    'standard': 'gpt-4o-mini',
    'premium': 'gpt-4o',
    'pro': 'gpt-4-turbo'
  }
  
  return defaults[tier] || 'gpt-4o-mini'
}
